/**
 * Solar Integration Types
 * Types for solar generation data and configuration
 */

// Solar generation data point (hourly)
export interface SolarDataPoint {
  timestamp: string;
  generation_kw: number;
}

// Solar generation data summary (from file upload)
export interface SolarGenerationData {
  totalRecords: number;
  avgGeneration: number;
  peakGeneration: number;
  totalGenerationKwh: number;
  capacityFactor: number;
  startDate: string;
  endDate: string;
  data: SolarDataPoint[];
  // Statistics
  statistics?: {
    daily_avg_kwh: number;
    monthly_totals: Array<{ month: string; generation_kwh: number }>;
    peak_hour: string;
    peak_generation_kw: number;
  };
}

// Solar system configuration
export interface SolarConfig {
  // Toggle
  solarEnabled: boolean;

  // System sizing
  capacityKwp: number;              // kWp installed
  inverterSizeKw: number;           // kW inverter capacity

  // Financial parameters (all in USD)
  capexPerKwp: number;              // USD/kWp
  opexPerKwpYear: number;           // USD/kWp/year

  // Technical parameters
  degradationRate: number;          // % annual (e.g., 0.5)
  lifetimeYears: number;            // years (e.g., 25)

  // Additional costs saved when self-consuming
  transportCostPerKwh: number;      // USD/kWh
  distributionCostPerKwh: number;   // USD/kWh
  otherVariableCostsPerKwh: number; // USD/kWh
}

// Default solar configuration
export const DEFAULT_SOLAR_CONFIG: SolarConfig = {
  solarEnabled: false,
  capacityKwp: 0,
  inverterSizeKw: 0,
  capexPerKwp: 900,                  // USD/kWp typical
  opexPerKwpYear: 12,                // USD/kWp/year typical
  degradationRate: 0.5,              // 0.5% annual
  lifetimeYears: 25,
  transportCostPerKwh: 0.02,         // USD/kWh
  distributionCostPerKwh: 0.015,     // USD/kWh
  otherVariableCostsPerKwh: 0.005,   // USD/kWh
};

// Solar savings breakdown (from optimization)
export interface SolarSavingsBreakdown {
  // Direct self-consumption savings
  directSelfConsumption: {
    energyKwh: number;
    energySavingsUsd: number;
    transportSavingsUsd: number;
    distributionSavingsUsd: number;
    otherSavingsUsd: number;
    totalSavingsUsd: number;
  };

  // Via BESS (solar stored then discharged)
  viaBess: {
    chargedKwh: number;              // Solar energy stored in BESS
    dischargedKwh: number;           // Solar energy discharged from BESS
    savingsUsd: number;              // Savings from solar discharge
  };

  // Curtailment (lost solar)
  curtailment: {
    energyKwh: number;
    potentialLossUsd: number;
  };

  // Grid arbitrage (BESS charged from grid)
  gridArbitrage: {
    chargedKwh: number;
    dischargedKwh: number;
    savingsUsd: number;
  };

  // Totals
  totalSolarSavingsUsd: number;
  totalBessSavingsUsd: number;
  totalCombinedSavingsUsd: number;
}

// Energy origin tracking in BESS
export interface BESSEnergyOrigin {
  totalSocKwh: number;
  solarEnergyKwh: number;
  gridEnergyKwh: number;
  solarRatio: number;               // 0-1, proportion of solar in BESS
  gridRatio: number;                // 0-1, proportion of grid in BESS
  avgGridChargePriceUsd: number;    // Weighted average price of grid energy
}

// Hourly simulation result with solar
export interface HourlySolarSimulationResult {
  timestamp: string;

  // Generation and consumption
  solarGenerationKw: number;
  demandKw: number;

  // Solar energy flows
  solarSelfConsumptionKwh: number;  // Solar -> Direct consumption
  solarToBessKwh: number;           // Solar -> BESS
  solarCurtailedKwh: number;        // Solar lost (BESS full)

  // BESS flows with origin tracking
  bessDischargeKwh: number;         // Total discharge
  bessDischargeSolarKwh: number;    // Discharge from solar origin
  bessDischargeGridKwh: number;     // Discharge from grid origin
  bessChargeFromGridKwh: number;    // Grid -> BESS

  // BESS state
  bessSocKwh: number;
  bessSolarRatio: number;

  // Grid purchase
  gridPurchaseKwh: number;

  // Prices
  spotPriceUsd: number;

  // Savings breakdown
  savingsAutoconsumo: number;       // USD - direct solar savings
  savingsBessSolar: number;         // USD - solar via BESS savings
  savingsBessArbitrage: number;     // USD - grid arbitrage savings
  savingsTotal: number;             // USD - total hour savings
}

// Financial metrics by component
export interface SolarFinancialMetrics {
  // Investment
  solarCapex: number;
  bessCapex: number;
  totalCapex: number;

  // Annual operating costs
  solarOpexYear1: number;
  bessOpexYear1: number;

  // Savings by component (year 1)
  autoconsumoSavingsYear1: number;
  bessSolarSavingsYear1: number;
  bessArbitrageSavingsYear1: number;
  peakShavingSavingsYear1: number;
  otherSavingsYear1: number;

  // Total metrics
  totalSavingsYear1: number;
  netCashFlowYear1: number;

  // NPV/IRR by component (with proportional CAPEX allocation)
  componentMetrics: {
    autoconsumo: { vanUsd: number; tir: number; paybackYears: number };
    bessSolar: { vanUsd: number; tir: number; paybackYears: number };
    bessArbitrage: { vanUsd: number; tir: number; paybackYears: number };
    peakShaving: { vanUsd: number; tir: number; paybackYears: number };
  };

  // System totals
  vanTotalUsd: number;
  tirTotal: number;
  paybackYearsTotal: number;

  // Synergy analysis
  synergy: {
    vanSynergyUsd: number;          // Additional VAN from combining
    tirImprovement: number;         // % points improvement
    paybackReduction: number;       // Years saved
  };
}

// Cash flow entry for annual analysis
export interface AnnualCashFlowEntry {
  year: number;

  // Investments (negative in year 0)
  capexBess: number;
  capexSolar: number;

  // Savings/Revenues by component
  autoconsumoDirecto: number;
  bessSolar: number;
  bessArbitraje: number;
  peakShavingOther: number;

  // Operating costs (negative)
  opexBess: number;
  opexSolar: number;

  // Net
  netCashFlow: number;
  cumulativeCashFlow: number;
}

// Monthly cash flow for detailed first year
export interface MonthlyCashFlowEntry {
  month: string;                    // "Ene", "Feb", etc.

  // Solar savings breakdown
  autoconsumoEnergia: number;
  autoconsumoTransporte: number;
  bessDescargaSolar: number;

  // BESS savings
  bessDescargaGrid: number;
  bessArbitraje: number;

  // Other
  peakShavingOther: number;

  // Costs
  opexMensual: number;

  // Net
  netCashFlow: number;
}
